import { useOrderStatusUpdateFlow } from '../../hooks/orders/useOrderStatusUpdateFlow';
import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../components/CatalogListHeader/CatalogListHeader';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import OrderListViewMenu from '../../components/OrderListViewMenu/OrderListViewMenu';
import {
  ActionIcon,
  Button,
  Card,
  Drawer,
  Flex,
  Grid,
  Menu,
  Select,
  Text,
  TextInput,
  Tooltip,
  rem
} from '@mantine/core';
import { useLocation } from 'react-router-dom';
import {
  IconFileSpreadsheet,
  IconInfoCircle,
  IconListDetails,
  IconMoneybag,
  IconTruckDelivery,
  IconWashMachine
} from '@tabler/icons-react';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import useDownloadExcel from '../../hooks/useDownloadExcel/useDownloadExcel';
import { formatMoneyString } from '../../utils/strings';
import { IOrder } from '../../interfaces/Order';
import OrderStatuses from '../../enum/OrderStatuses';
import { useDisclosure } from '@mantine/hooks';
import OrderProductsModal from '../../components/OrderModals/OrderProductsModal';
import OrderPaymentLinesModal from '../../components/OrderModals/OrderPaymentLinesModal';
import { showNotification } from '../../utils/Notification/Notification';
import {
  useCreateOrderEvidence,
  useSendEmailReceipt,
  useSetOrderCancelled,
  useSetOrderCleaned
} from '../../hooks/orders/mutations';
import { exportOrdersListV2 } from '../../services/order.service';
import { downloadTicketAsPNG, tryPrintTicket } from '../../utils/ticketHelper';
import { useSelector } from 'react-redux';
import { modals } from '@mantine/modals';
import OrderCleanedByDateReport from '../../components/OrderModals/OrderCleanedByDateReport';
import CleaningOrderTable from '../../components/CleaningOrdersViews/CleaningOrderTable';
import CleaningOrderCards from '../../components/CleaningOrdersViews/CleaningOrderCards';
import { MRT_PaginationState } from 'mantine-react-table';
import UploadOrderEvidenceFile from '../NewOrder/components/UploadOrderEvidenceFile';
import OrderEvidencesCarouselModal from '../../components/OrderModals/OrderEvidencesCarouselModal';
import SkeletonLine from '../../components/Skeletons/SkeletonLine';
import { useOrdersByStatusQueries } from '../../hooks/ordersByStatusPage';
import PaymentLinesByDateReport from '../../components/OrderModals/PaymentLinesByDateReport';
import ScheduledDeliveriesByDateReport from '../../components/OrderModals/ScheduledDeliveriesByDateReport';
import ServicesToDeliverByDateReport from '../../components/OrderModals/ServicesToDeliverByDateReport';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { useOrderPaymentLinesModal } from '../../hooks/useOrderPaymentLinesModal';
import { useGetStoreAssignableStaff } from '../../hooks/staff/queries';
import {
  getEmployeeDisplayName,
  normalizeAssignedEmployeeFilter
} from '../../utils/orderLineAssignments';
import { useStoreScopedAssignedEmployeeFilter } from './useStoreScopedAssignedEmployeeFilter';

const CleaningOrders = () => {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'Notifications',
    'OrdersPage',
    'CleaningPage',
    'UpdateOrderForm',
    'ReportsPage',
    'OrdersHeaderSummary'
  ]);
  const { company, user } = useSelector((state) => state.user);
  const hideOrderTotals = user?.permission['hideOrderTotals']?.canView;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const searchTermInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const { assignedEmployeeFilter, setAssignedEmployeeFilter } =
    useStoreScopedAssignedEmployeeFilter(searchParamStoreId);
  const orderStatusFilter = OrderStatuses.CLEANING;
  const [showTableView, setShowTableView] = useState(false);
  const setSearchParamsDebounced = debounce((val: string) => {
    setSearchTerm(val || undefined);
  }, 500);
  const queryClient = useQueryClient();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [openedOrderProductsModal, orderProductsModalhandlers] = useDisclosure(false);
  const [openedOrderEvidenceModal, evidenceModalhandlers] = useDisclosure(false);
  const [openedScheduledDeliveriesDrawer, scheduledDeliveriesDrawerHandlers] = useDisclosure(false);
  const [scheduledDeliveriesDate, setScheduledDeliveriesDate] = useState<string>('');
  const [openedServicesToDeliverDrawer, servicesToDeliverDrawerHandlers] = useDisclosure(false);
  const [servicesToDeliverDate, setServicesToDeliverDate] = useState<string>('');
  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
  const sendEmailReceipt = useSendEmailReceipt();
  const setOrderCleaned = useSetOrderCleaned();
  const setOrderCancelled = useSetOrderCancelled();
  const createOrderEvidence = useCreateOrderEvidence();
  const { data: assignableStaffs = [] } = useGetStoreAssignableStaff(
    !!searchParamStoreId,
    searchParamStoreId,
    'appendAdmin=true'
  );
  const assignedEmployeeQuery = normalizeAssignedEmployeeFilter(assignedEmployeeFilter);
  const assignedEmployeeOptions = useMemo(
    () => [
      {
        value: 'all',
        label: t('OrdersPage:filters.all')
      },
      ...assignableStaffs
        .filter((staff) => !!staff._id)
        .map((staff) => ({
          value: staff._id!,
          label: getEmployeeDisplayName(staff)
        }))
    ],
    [assignableStaffs, t]
  );

  const limitPerReq = 9;
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });

  const { store, orders, summary } = useOrdersByStatusQueries({
    storeId: searchParamStoreId,
    searchTerm,
    orderStatusFilter,
    showTableView,
    pagination,
    limitPerReq,
    assignedEmployee: assignedEmployeeQuery
  });

  const {
    data: ordersData,
    fetchNextPage,
    hasNextPage,
    refetch: refetchList,
    isLoading: isLoadingOrders,
    error: ordersError
  } = orders;

  const { data: storeData } = store;
  const paymentLinesModal = useOrderPaymentLinesModal({ store: storeData });
  const { data: summaryData, isLoading: isGetOrdersHeaderSummaryLoading } = summary;

  const statusUpdate = useOrderStatusUpdateFlow({
    storeId: searchParamStoreId,
    status: 'cleaning',
    store: storeData,
    assignedEmployee: assignedEmployeeQuery,
    assignedEmployeeLabel: assignedEmployeeOptions.find(option => option.value === assignedEmployeeFilter)?.label,
    contextKey: searchTerm,
    onUpdated: async () => { await Promise.all([refetchList(), summary.refetch()]); }
  });

  const ordersList = useMemo(() => {
    return ordersData?.pages?.reduce((list: IOrder[], page: { orders: IOrder[] }) => {
      list.push(...page.orders);
      return list;
    }, [] as IOrder[]);
  }, [ordersData]);

  const totalRowsCount = useMemo(
    () => ordersData?.pages[0]?.totalRows[0]?.totalRowsCount || -1,
    [ordersData]
  );
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  const showOrderProductsModal = (order: IOrder) => {
    setSelectedOrder(order);
    orderProductsModalhandlers.open();
  };

  const onAddPaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {}
  };

  const showPaymentLinesModal = async (order: IOrder) => {
    setSelectedOrder(order);
    await paymentLinesModal.open(order);
  };

  const onDeletePaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const onUpdatePaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const onSendEmailReceipt = async (orderId: string) => {
    try {
      if (sendEmailReceipt.isPending) return;
      await sendEmailReceipt.mutateAsync(orderId);
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const clearSearchTerm = () => {
    if (searchTermInputRef.current) {
      searchTermInputRef.current.value = ''; // Reset the input field value via ref
      setSearchParamsDebounced(undefined); // Reset the state to trigger re-render
      setAssignedEmployeeFilter(null);
    }
  };

  const onPrintTicket = async (order: IOrder) => {
    try {
      tryPrintTicket(order, order.createdIn, t, 'ticketStructure', company.logoUrl, false, company?.companyTaxInfo);
    } catch (e) {
      console.log('[ERROR] onPrintTicket:', e);
    }
  };

  const onDownloadPNGTicket = async (order: IOrder) => {
    try {
      downloadTicketAsPNG(order, order.createdIn, t, 'ticketStructure', company.logoUrl);
    } catch (e) {
      console.log('[ERROR] onDownloadPNGTicket:', e);
    }
  };

  const showOrderEvidenceModal = (order: IOrder) => {
    setSelectedOrder(order);
    evidenceModalhandlers.open();
  };

  const handleEvidenceDeleted = (evidenceId: string) => {
    setSelectedOrder((prev) =>
      prev
        ? {
            ...prev,
            evidence: prev.evidence?.filter((evidence) => evidence._id !== evidenceId) ?? []
          }
        : prev
    );
  };

  const onCreateOrderEvidence = async (orderId: string, data: FormData) => {
    try {
      if (createOrderEvidence.isPending) return;
      await createOrderEvidence.mutateAsync({ orderId, data });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      modals.close('uploadEvidenceModal');
      await refetchList();
      return true;
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      return false;
    }
  };

  const onShowCreateOrderEvidenceModal = (order: IOrder) => {
    modals.open({
      size: 'xl',
      modalId: 'uploadEvidenceModal',
      title: `${t('OrdersPage:evidenceModal.title')} - ${buildOrderFolio(
        order?.folioPrefix,
        order?.sequence
      )}`,
      children: (
        <>
          <UploadOrderEvidenceFile
            onSubmit={async (values: any, formData: any) => {
              return (await onCreateOrderEvidence(order._id || '', formData)) || false;
            }}
          />
        </>
      )
    });
  };

  const onOrderCleaned = async ({
    sequence,
    storeId,
    params
  }: {
    sequence: string;
    storeId: string;
    params: {
      markOutsourcedAsReturned?: boolean;
    };
  }) => {
    try {
      if (setOrderCleaned.isPending) return;
      await setOrderCleaned.mutateAsync({
        sequence: sequence,
        storeId: storeId || '',
        data: {
          cleanedDateTime: new Date().toISOString(),
          readyDateTime: new Date().toISOString()
        },
        params: params
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      summary.refetch();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderCancelled = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderCancelled.isPending) return;
      await setOrderCancelled.mutateAsync({
        sequence: sequence,
        storeId: storeId,
        data: {
          cancelledDateTime: new Date().toISOString()
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onUpdateOrdersModal = statusUpdate.open;

  const onShowCleanedOrdersReport = async (dateStr: string) => {
    modals.open({
      size: '100%',
      modalId: 'cleanedOrdersReport',
      title: `${t('ReportsPage:cleanedOrders.title')} - ${moment(dateStr).format('DD/MM/YYYY')}`,
      children: (
        <>
          <OrderCleanedByDateReport
            close={() => modals.close('cleanedOrdersReport')}
            storeId={searchParamStoreId}
            date={dateStr}
          />
        </>
      )
    });
  };

  const onShowPaymentsByDayReport = async (dateStr: string) => {
    modals.open({
      size: '100%',
      modalId: 'paymentsByDayReport',
      title: `${t('OrdersPage:paymentsByDayModal.title')}`,
      children: (
        <>
          <PaymentLinesByDateReport
            close={() => modals.close('paymentsByDayReport')}
            storeId={searchParamStoreId}
            date={dateStr}
          />
        </>
      )
    });
  };

  const onShowScheduledDeliveriesReport = async (dateStr: string) => {
    setScheduledDeliveriesDate(dateStr);
    scheduledDeliveriesDrawerHandlers.open();
  };

  const onShowServicesToDeliverReport = async (dateStr: string) => {
    setServicesToDeliverDate(dateStr);
    servicesToDeliverDrawerHandlers.open();
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['orders']
      });
    };
  }, []);

  useEffect(() => {
    if (ordersData && selectedOrder) {
      const updatedOrder = ordersList?.find((order: IOrder) => order._id === selectedOrder._id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
        paymentLinesModal.syncOrder(updatedOrder);
      }
    }
  }, [ordersData, selectedOrder?._id, ordersList]);

  return (
    <>
      <OrderEvidencesCarouselModal
        title={`${t('OrdersPage:evidenceModal.title')}: ${buildOrderFolio(
          selectedOrder?.folioPrefix,
          selectedOrder?.sequence
        )}`}
        close={() => {
          evidenceModalhandlers.close();
        }}
        opened={openedOrderEvidenceModal}
        order={selectedOrder}
        onEvidenceDeleted={handleEvidenceDeleted}
      />
      <OrderPaymentLinesModal
        onAddPaymentLine={onAddPaymentLine}
        title={paymentLinesModal.title}
        close={paymentLinesModal.close}
        opened={paymentLinesModal.opened}
        order={paymentLinesModal.order}
        onDeletePaymentLine={onDeletePaymentLine}
        onUpdatePaymentLine={onUpdatePaymentLine}
        pinUser={paymentLinesModal.pinUser}
      />
      <OrderProductsModal
        title={`${t('OrdersPage:productsModal.title')}: ${buildOrderFolio(
          selectedOrder?.folioPrefix,
          selectedOrder?.sequence
        )}`}
        close={() => {
          orderProductsModalhandlers.close();
        }}
        opened={openedOrderProductsModal}
        order={selectedOrder}
      />
      <CatalogContainer pos="relative">
        <CatalogListHeader title={t('CleaningPage:title')} showCreateButton={false} />
        <LoaderOverlay
          visible={
            setOrderCleaned.isPending ||
            setOrderCancelled.isPending ||
            statusUpdate.isPending
          }
        />
        <Grid>
          <Grid.Col span={12} my={10}>
            <Grid align="center" justify="center">
              <Grid.Col span={{ xs: 12, md: 'auto' }}>
                <TextInput
                  ref={searchTermInputRef}
                  onChange={(event) => setSearchParamsDebounced(event.currentTarget.value)}
                  placeholder={`${t('FieldName:order')}, ${t('FieldName:customer')}, ${t(
                    'FieldName:phone'
                  )}`}
                  label={`${t('FieldName:order')}`}
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 3 }}>
                <Select
                  label={
                    <Flex align="center" gap={4}>
                      <span>{t('OrdersPage:filters.assignedEmployee')}</span>
                      <Tooltip
                        withinPortal={false}
                        label={t('OrdersPage:filters.assignedEmployeeHelper')}
                        multiline
                        maw={260}
                        withArrow>
                        <IconInfoCircle
                          size={14}
                          stroke={1.8}
                          style={{ cursor: 'help', flexShrink: 0 }}
                        />
                      </Tooltip>
                    </Flex>
                  }
                  placeholder={t('OrdersPage:filters.assignedEmployee')}
                  data={assignedEmployeeOptions}
                  value={assignedEmployeeFilter || 'all'}
                  onChange={(value) => {
                    setAssignedEmployeeFilter(value === 'all' ? null : value);
                  }}
                  allowDeselect={false}
                  searchable
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
                <Button fullWidth type="button" color="gray" onClick={clearSearchTerm}>
                  {t('CommonButtons:clear').toUpperCase()}
                </Button>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
                <Flex justify="flex-end" align="center" gap="xs" wrap="wrap">
                  <Tooltip withinPortal={false} label={t('CommonButtons:exportList')}>
                    <ActionIcon
                      disabled={loadingExport}
                      onClick={() => {
                        downloadExcelV2(async () => {
                          try {
                            return await exportOrdersListV2({
                              q: searchTerm,
                              status: orderStatusFilter,
                              storeId: searchParamStoreId,
                              assignedEmployee: assignedEmployeeQuery
                            });
                          } catch (e) {
                            throw e;
                          }
                        }, `Ordenes_${searchParamStoreId}`);
                      }}
                      variant="filled"
                      color="#1D6F42"
                      aria-label="Descargar_Listado_CSV_Ordenes">
                      <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                  <OrderListViewMenu
                    showTableView={showTableView}
                    onShowTableViewChange={setShowTableView}
                    storeId={searchParamStoreId}
                    disabled={loadingExport}
                  />
                </Flex>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
        <Grid
          pos={'sticky'}
          top={'calc(var(--app-shell-header-offset, 0rem) + var(--app-shell-padding))'}
          justify="center"
          align="center"
          style={{
            zIndex: 100
          }}>
          <Grid.Col span={12} pt={0}>
            <Card mt={0}>
              <Grid gutter={'md'}>
                <Grid.Col span={{ xs: 12, md: 4 }}>
                  <Grid>
                    <Grid.Col span={6}>
                      <Button fullWidth onClick={onUpdateOrdersModal} type="button">
                        Actualizar Pedido
                      </Button>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Menu shadow="md" width={200}>
                        <Menu.Target>
                          <Button fullWidth>Reportes</Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Label>Reportes</Menu.Label>
                          <Menu.Item
                            onClick={() =>
                              onShowCleanedOrdersReport(moment().startOf('day').toISOString())
                            }
                            leftSection={
                              <IconWashMachine style={{ width: rem(14), height: rem(14) }} />
                            }>
                            Procesado Hoy
                          </Menu.Item>
                          <Menu.Item
                            onClick={() =>
                              onShowCleanedOrdersReport(
                                moment().add('day', -1).startOf('day').toISOString()
                              )
                            }
                            leftSection={
                              <IconWashMachine style={{ width: rem(14), height: rem(14) }} />
                            }>
                            Procesado Ayer
                          </Menu.Item>
                          {!hideOrderTotals && (
                            <Menu.Item
                              onClick={() =>
                                onShowPaymentsByDayReport(moment().startOf('day').toISOString())
                              }
                              leftSection={
                                <IconMoneybag style={{ width: rem(14), height: rem(14) }} />
                              }>
                              {t('OrdersPage:paymentsByDayModal.tooltip')}
                            </Menu.Item>
                          )}
                          <Menu.Item
                            onClick={() =>
                              onShowScheduledDeliveriesReport(moment().startOf('day').toISOString())
                            }
                            leftSection={
                              <IconTruckDelivery style={{ width: rem(14), height: rem(14) }} />
                            }>
                            {t('ReportsPage:scheduledDeliveriesReport.title')}
                          </Menu.Item>
                          <Menu.Item
                            onClick={() =>
                              onShowServicesToDeliverReport(moment().startOf('day').toISOString())
                            }
                            leftSection={
                              <IconListDetails style={{ width: rem(14), height: rem(14) }} />
                            }>
                            {t('ReportsPage:servicesToDeliverReport.title')}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Grid.Col>
                  </Grid>
                </Grid.Col>
                <Grid.Col span={{ xs: 12, md: 8 }}>
                  <Grid justify="left" align="center">
                    <Grid.Col span={3}>
                      <Text fw={'bold'}>
                        {isGetOrdersHeaderSummaryLoading ? (
                          <SkeletonLine />
                        ) : (
                          <>
                            {t('OrdersHeaderSummary:orders')}: {summaryData?.ordersTotal}
                          </>
                        )}
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Text fw={'bold'}>
                        {isGetOrdersHeaderSummaryLoading ? (
                          <SkeletonLine />
                        ) : (
                          <>
                            {t('OrdersHeaderSummary:pieces')}:{' '}
                            {(summaryData?.totalPieces || 0).toFixed(2)}
                          </>
                        )}
                      </Text>
                    </Grid.Col>
                    {!hideOrderTotals && (
                      <>
                        <Grid.Col span={3}>
                          <Text fw={'bold'}>
                            {isGetOrdersHeaderSummaryLoading || summary.isFetching ? (
                              <SkeletonLine />
                            ) : (
                              <>
                                {t('OrdersHeaderSummary:total')}:{' '}
                                {formatMoneyString((summaryData?.total || 0).toFixed(2))}
                              </>
                            )}
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <Text fw={'bold'}>
                            {isGetOrdersHeaderSummaryLoading ? (
                              <SkeletonLine />
                            ) : (
                              <>
                                {t('OrdersHeaderSummary:amountDue')}:{' '}
                                {formatMoneyString((summaryData?.unpaidTotal || 0).toFixed(2))}
                              </>
                            )}
                          </Text>
                        </Grid.Col>
                      </>
                    )}
                  </Grid>
                </Grid.Col>
              </Grid>
            </Card>
          </Grid.Col>
        </Grid>
        {showTableView && (
          <CleaningOrderTable
            orders={ordersList}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={!!ordersError}
            isLoading={isLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
            searchParamStoreId={searchParamStoreId}
            onOrderCleaned={onOrderCleaned}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
            onShowOrderEvidenceModal={showOrderEvidenceModal}
          />
        )}
        {!showTableView && (
          <CleaningOrderCards
            orders={ordersList}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            searchParamStoreId={searchParamStoreId}
            isLoadingOrders={isLoadingOrders}
            onOrderCleaned={onOrderCleaned}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            onDownloadPNGTicket={onDownloadPNGTicket}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
            onShowCreateOrderEvidenceModal={onShowCreateOrderEvidenceModal}
            onShowOrderEvidenceModal={showOrderEvidenceModal}
          />
        )}
      </CatalogContainer>
      <Drawer
        opened={openedScheduledDeliveriesDrawer}
        onClose={scheduledDeliveriesDrawerHandlers.close}
        title={t('ReportsPage:scheduledDeliveriesReport.title')}
        size="100%"
        position="right"
      >
        <ScheduledDeliveriesByDateReport
          close={scheduledDeliveriesDrawerHandlers.close}
          storeId={searchParamStoreId || 'all'}
          companyId={company._id}
          date={scheduledDeliveriesDate}
        />
      </Drawer>
      <Drawer
        opened={openedServicesToDeliverDrawer}
        onClose={servicesToDeliverDrawerHandlers.close}
        title={t('ReportsPage:servicesToDeliverReport.title')}
        size="100%"
        position="right"
      >
        <ServicesToDeliverByDateReport
          close={servicesToDeliverDrawerHandlers.close}
          storeId={searchParamStoreId || 'all'}
          companyId={company._id}
          date={servicesToDeliverDate}
        />
      </Drawer>
    </>
  );
};

export default CleaningOrders;
